import { get } from "lodash-es";

import getObjectHash from "../hash/getObjectHash.js";
import { parse } from "../parsing/index.js";
import type Endpoint from "./Endpoint.js";
import type { PathParamsBase, QueryParamsBase } from "./Endpoint.js";

export default class OutgoingRequest<
    PathParams extends PathParamsBase,
    QueryParams extends QueryParamsBase,
    Body,
    Result,
    Deserialized = Result,
> {
    pathParams: PathParams | undefined;
    queryParams: QueryParams | undefined;
    body: Body | undefined;
    deserializer: ((result: Result) => Deserialized) | undefined;
    onSuccessCallbacks: ((result: Deserialized) => void)[] = [];

    constructor(readonly endpoint: Endpoint<PathParams, QueryParams, Body, any, Result>) {}

    setPathParams(pathParams: unknown extends PathParams ? never : PathParams) {
        this.pathParams = pathParams;
        return this;
    }

    setQueryParams(queryParams: unknown extends QueryParams ? never : QueryParams) {
        this.queryParams = queryParams;
        return this;
    }

    setBody(body: unknown extends Body ? never : Body) {
        this.body = body;
        return this;
    }

    parseResult(result: unknown): Deserialized {
        const parsed = parse(result, this.endpoint.getResultParser());
        const deserialized = this.deserializer
            ? this.deserializer(parsed)
            : (parsed as unknown as Deserialized);
        this.onSuccessCallbacks.forEach((callback) => callback(deserialized));
        return deserialized;
    }

    setDeserializer<D>(deserializer: (result: Result) => D) {
        const output = new OutgoingRequest<PathParams, QueryParams, Body, Result, D>(this.endpoint);
        output.pathParams = this.pathParams;
        output.queryParams = this.queryParams;
        output.body = this.body;
        output.deserializer = deserializer;
        return output;
    }

    onSuccess(callback: (result: Deserialized) => void) {
        this.onSuccessCallbacks.push(callback);
        return this;
    }

    getPath() {
        const fullPath = this.endpoint.getPath();
        const output = fullPath.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_, name: string) => {
            const value = get(this.pathParams, name);
            if (value === undefined) throw new Error(`Missing path param "${name}"`);
            return encodeURIComponent(value);
        });
        return output;
    }

    getHash() {
        return getObjectHash({
            method: this.endpoint.getMethod(),
            path: this.getPath(),
            queryParams: this.queryParams,
            body: this.body,
            pathParams: this.pathParams,
        });
    }
}
